import {
  Button,
  ButtonGroup,
  DataTable,
  DataTableWC,
  Icon,
  PageBody,
  PageSection,
  Search,
  Spacer,
  Stack,
  Text,
  TextTitle,
} from '@pypestream/design-system';
import {
  dateFnsLocales,
  fallbackLanguage,
  TranslationComponent,
  useTranslation,
} from '@pypestream/translations';
import { formatTimeWithTimezone } from '@pypestream/utils';
import { html } from 'lit/static-html.js';
import React, { useCallback, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router';

import { Loader } from '../../../components';
import {
  ListLoadError,
  ListLoadLiveRegion,
} from '../../../components/list-load-status/list-load-status';
import {
  MANAGER_SURFACE_ROLES,
  useAuthRole,
  useUserTimeZone,
} from '../../../hooks';
import {
  sendManagerEvent,
  useManagerCtxSelector,
  useManagerStateMatches,
  useManagerStateMatchesAllOf,
} from '../../../xstate/app.xstate';
import { useRetryableListLoad } from '../../../hooks/use-retryable-list-load';
import { IPage } from '../../types';
import { NEW_TEAM_TEMP_ID } from '../../../xstate/team-details-xstate-helper';
import { debounce } from 'lodash';
import { SEARCH_DEBOUNCE_TIME } from '../../../utils/constants';

export const Teams: React.FC<IPage> = ({ title }) => {
  const params = useParams();
  const loaded = useManagerStateMatches('orgRelated.ready.teams.loaded');
  const failed = useManagerStateMatches('orgRelated.ready.teams.error');
  const isReadyToLoad = useManagerStateMatchesAllOf([
    'orgRelated.ready.teams.idle',
    'orgRelated.ready.currentOrg.selected',
  ]);

  const { orgId, teams, defaultLanguage } = useManagerCtxSelector((ctx) => ({
    orgId: ctx.selectedOrgId,
    teams: ctx.teams,
    defaultLanguage: ctx.userInfo?.defaultLanguage,
  }));
  const isParamsSynced = orgId === params.org_id;

  const userTimeZone = useUserTimeZone();
  const navigate = useNavigate();

  const tableRef = useRef<DataTableWC>(null);
  const [t] = useTranslation();
  // Same set the Teams route is gated with (MANAGER_SURFACE_ROLES). Since
  // PSTUDIO-4099 the route itself refuses a role-less user, so this in-page
  // check is now defense-in-depth rather than the primary gate.
  const isAdminOrManagerRole = useAuthRole(MANAGER_SURFACE_ROLES);

  useEffect(() => {
    if (isReadyToLoad && isParamsSynced) {
      sendManagerEvent('manager.teams.loadTeams');
    }
  }, [isReadyToLoad, isParamsSynced]);

  // PSTUDIO-4442. The effect above fires only from `teams.idle`, so once a load
  // failed the machine stayed in `teams.error` and the page showed the loader
  // until a manual refresh. The error view carries a retry instead.
  const requestLoad = useCallback(
    () => sendManagerEvent('manager.teams.loadTeams'),
    []
  );
  const {
    view,
    showLoader,
    showError,
    busyRetry,
    announcement,
    retry,
    recoveredSectionRef,
  } = useRetryableListLoad({ loaded, failed, requestLoad });

  const loadErrorTitle =
    t('manager/teams:teamsList.loadError.title', {
      defaultValue: "We couldn't load the teams",
    }) || '';

  // Only the string the current announcement needs is looked up.
  const announcementText = (() => {
    switch (announcement) {
      case 'error':
        return loadErrorTitle;
      case 'retrying':
        return (
          t('manager/teams:teamsList.loadError.retrying', {
            defaultValue: 'Retrying…',
          }) || ''
        );
      case 'recovered':
        return (
          t('manager/teams:teamsList.loadError.recovered', {
            defaultValue: 'Teams loaded',
          }) || ''
        );
      default:
        return '';
    }
  })();

  return (
    <>
      <PageBody background="none">
        <>
          <PageSection size="small">
            <ListLoadLiveRegion
              text={announcementText}
              testId="teams-live-region"
            />
            {showLoader && (
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Loader relative />
              </div>
            )}
            {showError && (
              <ListLoadError
                title={loadErrorTitle}
                body={
                  t('manager/teams:teamsList.loadError.body', {
                    defaultValue:
                      'Something went wrong while loading this list.',
                  }) || ''
                }
                retryLabel={
                  t('manager/teams:teamsList.loadError.retry', {
                    defaultValue: 'Try again',
                  }) || ''
                }
                busy={busyRetry}
                onRetry={retry}
                testId="teams-load-error"
                retryTestId="teams-retry-btn"
              />
            )}
            {view === 'ready' && (
              // A plain div: the DS wrappers do not accept `tabIndex`, and focus
              // has to land on a native node after a successful retry.
              <div ref={recoveredSectionRef} tabIndex={-1}>
                <Stack alignItems="center" justifyContent="space-between">
                  <TextTitle size="xsmall">
                    <TranslationComponent i18nKey="manager/teams:teamsList.teams">
                      Teams
                    </TranslationComponent>
                  </TextTitle>
                  <Stack
                    alignItems="center"
                    justifyContent="end"
                    gutter="small"
                  >
                    <Search
                      size="large"
                      style={{ maxWidth: '240px' }}
                      onInput={debounce((e) => {
                        tableRef.current?.table.setGlobalFilter(
                          String(e.target?.value) || ''
                        );
                      }, SEARCH_DEBOUNCE_TIME)}
                    />
                    {isAdminOrManagerRole && (
                      <ButtonGroup>
                        <Button
                          size="large"
                          onClick={() => {
                            navigate(
                              `/organization/${orgId}/teams/${NEW_TEAM_TEMP_ID}`
                            );
                          }}
                        >
                          <Icon slot="prefix" name="add" size="xsmall" />
                          <TranslationComponent i18nKey="manager/teams:teamsList.createTeam">
                            Create Team
                          </TranslationComponent>
                        </Button>
                      </ButtonGroup>
                    )}
                  </Stack>
                </Stack>
                <Spacer size="large"></Spacer>
                <DataTable
                  ref={tableRef}
                  size="small"
                  filterColumns={['name', 'users']}
                  columns={[
                    {
                      accessorKey: 'name',
                      accessorFn: (row) => row.name,
                      header: () =>
                        t('manager/teams:teamsList.table.name', {
                          defaultValue: 'Name',
                        }) || '',
                      cell: ({ row, renderValue }) => html`
                        <div style="max-width: 400px">
                          <ps-text-subtitle size="small" truncate
                            >${row.original?.name}</ps-text-subtitle
                          >
                          ${row.original?.description
                            ? html`<ps-text-caption
                                >${row.original?.description}</ps-text-caption
                              >`
                            : ''}
                        </div>
                      `,
                    },
                    {
                      accessorKey: 'users',
                      accessorFn: (row) =>
                        row.assignedMembers
                          ?.map((user) => `${user.firstName} ${user.lastName}`)
                          .join(', '),
                      header: () =>
                        t('manager/teams:teamsList.table.users', {
                          defaultValue: 'Users',
                        }) || '',
                      cell: ({ row, renderValue }) =>
                        html`<ps-avatar-group max="2" align="start">
                          ${row.original?.assignedMembers?.map(
                            (user) =>
                              html`<ps-avatar
                                label="${user.firstName}"
                                src="${user.picture || ''}"
                              ></ps-avatar>`
                          )}
                        </ps-avatar-group>`,
                    },
                    {
                      accessorKey: 'updated',
                      header: () =>
                        t('manager/teams:teamsList.table.updated', {
                          defaultValue: 'Last updated',
                        }) || '',
                      cell: ({ row, renderValue }) =>
                        html`<ps-text-body variant="secondary">
                          ${formatTimeWithTimezone({
                            fromDate: row.original.updatedAt,
                            locale:
                              dateFnsLocales[
                                defaultLanguage || fallbackLanguage
                              ],
                            userTimeZone,
                          })}</ps-text-body
                        >`,
                    },
                  ]}
                  data={teams}
                  onRowSelectionChange={(event) => {
                    if (event.detail.selected && event.detail.selected.length) {
                      event.detail.selected.map((item) => {
                        const dataTable = event.currentTarget as DataTableWC;

                        dataTable['table'].resetRowSelection();

                        sendManagerEvent({
                          type: 'manager.selectTeam',
                          id: item.original.id,
                        });

                        navigate(
                          `/organization/${orgId}/teams/${item.original.id}`
                        );

                        return item;
                      });
                    }
                  }}
                  enableRowSelection
                ></DataTable>

                {!teams ||
                  (!teams.length && (
                    <Text
                      size="xsmall"
                      textAlign="center"
                      className="u-margin-top-xlarge"
                    >
                      <TranslationComponent i18nKey="manager/teams:teamsList.createToGetStarted">
                        Create a team to get started.
                      </TranslationComponent>
                    </Text>
                  ))}
              </div>
            )}
          </PageSection>
        </>
      </PageBody>
    </>
  );
};
